import React from 'react'
import { useRouter } from 'next/router'
import modalController, {
  makeControlledModal
} from 'src/decorators/ModalController'
import Modal from '@jarvis.oddle/core/components/Modal'
import styled from 'styled-components'
import Div from '@jarvis.oddle/core/components/Div'
import Form from '@jarvis.oddle/core/components/Form'
import breakpoint from 'src/utils/styledComponentsBreakpoint'
import { TranslationContext } from '@jarvis.oddle/core/components/Translation'
import {
  buttonRadiusValues,
  buttonStyles,
  margin,
  marginSizes,
  padding,
  paddingSizes
} from '@jarvis.oddle/core/theme/default'
import { H2, LinkText } from '@jarvis.oddle/core/components/Typo'
import Flex from '@jarvis.oddle/core/components/Flex'
import pickUpDeliveryFlow from 'src/flows/pickUpDeliveryFlow'
import { formatDate, getTranslatedWeekDays } from 'src/utils/date'
import useTimer from '@jarvis.oddle/core/hooks/useTimer'
import OrderingFlowContext from 'src/containers/OrderingFlowContext'
import usePrevious from '@jarvis.oddle/core/hooks/usePrevious'
import { darken, isLight, lighten } from '@jarvis.oddle/core/tools/style'
import ModalContainer from '@jarvis.oddle/core/components/Modal/ModalContainer'
import useAppSelector from 'src/hooks/useAppSelector'
import { selectOrder } from 'src/redux/order'

const WrapperForm = styled(Div).attrs(() => ({
  width: '100%',
  as: Form
}))`
  display: contents;
`

// noinspection CssInvalidPropertyValue
const Container = styled(Modal.FlexContainer).attrs(() => ({
  width: '100%',
  height: '100%',
  flexDirection: 'column',
  padding: paddingSizes.medium
}))<{
  desktopHeight: number
}>`
  /* Fix Safari unable to calculate the height */
  /* stylelint-disable value-no-vendor-prefix */
  height: 30%;
  top: 100%;
  transform: translateX(-50%) translateY(-100%);
  min-height: 300px;
  /* stylelint-enable value-no-vendor-prefix */
  background: ${(props) => props.theme.colorContainerColor};
  overflow-y: auto;
  position: relative;
  box-shadow: 0 0 20px rgba(0, 0, 0, 0.2);
  ${breakpoint('desktop')`
    top: ${(props) => (props.desktopHeight === 130 ? '300px' : '260px')};
    left: 100%;
    width: 100%;
    max-width: 467px;
    height: 100%;
    max-height: 287px;
    box-shadow: 0px 0px 8px rgba(0, 0, 0, 0.2);
    border-radius: 15px;
    margin-left: -245px;
    transform: translateX(-50%) translateY(-50%);
  `}
`

const StyledModalBody = styled(Modal.Body)`
  color: ${(props) => props.theme.colorBodyFont};
  background-color: transparent;
  align-items: start;
  padding-top: 30px;
`

const StyledModalFooter = styled(Modal.Footer)`
  box-shadow: none;
  justify-content: flex-end;
  padding: 10px 0;
`

const StyledLinkText = styled(LinkText)`
  padding: ${padding[paddingSizes.small]};
  margin-right: ${margin[marginSizes.medium]};
  white-space: nowrap;
  outline: none;
`
const StyledFormSubmitButton = styled(Form.SubmitButton)<{
  showPreferredDateModal: boolean
}>`
  width: ${(props) => (props.showPreferredDateModal ? '193px' : '130px')};
  color: ${(props) => props.theme.buttonTextColor};
  background-color: ${(props) => props.theme.buttonColor};
  position: relative;
`
const StyledModalContainer = styled(ModalContainer)`
  display: block;
`

const ProgressBar = styled(Div)<{
  px: string
  buttonColor: string
  secondary: string
  buttonStyle: string
}>`
  border: none;
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: ${(props) => `${props.px}px`};
  transition: width 1s ease-in-out;
  background-color: ${(props) =>
    isLight(props.buttonColor)
      ? darken(
          props.secondary || props.buttonStyle === buttonStyles.outline
            ? 'transparent'
            : props.theme.buttonColor,
          15
        )
      : lighten(
          props.secondary || props.buttonStyle === buttonStyles.outline
            ? 'transparent'
            : props.theme.buttonColor,
          15
        )};
  opacity: 0.4;
  border-top-left-radius: ${(props) =>
    buttonRadiusValues[props.theme.buttonShape]};
  border-bottom-left-radius: ${(props) =>
    buttonRadiusValues[props.theme.buttonShape]};
  border-top-right-radius: ${(props) =>
    props.px < 130 ? '0' : buttonRadiusValues[props.theme.buttonShape]};
  border-bottom-right-radius: ${(props) =>
    props.px < 130 ? '0' : buttonRadiusValues[props.theme.buttonShape]};
  pointer-events: none;
`

interface ReminderOrderModalProps {
  modal: {
    onClose: (callback?: () => void) => void
    onClosed: () => void
    show: boolean
  }
}

const ReminderOrderModal = ({ modal }: ReminderOrderModalProps) => {
  const {
    isValidDateTime,
    resetAppointmentAction,
    showPreferredDateModal,
    desktopHeight,
    setShowPreferredDateModal
  } = React.useContext(OrderingFlowContext)
  const router = useRouter()
  const { t } = React.useContext(TranslationContext)
  const { storeOrder } = useAppSelector(selectOrder)
  const {
    collectionDate,
    collectionTime,
    isPickup,
    deliveryAddress,
    storeAddress
  } = storeOrder.data
  const durationInMs = 10000
  const [px, setPx] = React.useState<number>(0)
  const timer = useTimer(durationInMs)
  const prevRunning = usePrevious(timer.isRunning)

  const translatedDaysOfWeek = getTranslatedWeekDays(t)
  const translatedCollectionDate = translatedDaysOfWeek[new Date(collectionDate).getDay()]

  React.useEffect(() => {
    if (isValidDateTime && !timer.isRunning) {
      timer.start()
    }
  }, [timer, isValidDateTime])

  React.useEffect(() => {
    if (
      prevRunning === true &&
      timer.isRunning === false &&
      timer.timeLeft === 0
    ) {
      timer.stop()
      modal.onClose()
    }
  }, [timer, prevRunning, modal])

  React.useEffect(() => {
    if (timer.timeLeft > 0) {
      setPx(px + 13)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timer.timeLeft])

  const handlingFormSubmit = () => {
    if (showPreferredDateModal) {
      setShowPreferredDateModal(true)
      modal.onClose(() =>
        pickUpDeliveryFlow.start({
          type: 'dateTimeSelection',
          onFinished: () => modalController.showModal('CartDrawer')
        })
      )
      return
    }

    if (!isValidDateTime) {
      modal.onClose(() =>
        pickUpDeliveryFlow.start({
          onFinished: () => modalController.showModal('CartDrawer')
        })
      )
      return
    }

    modal.onClose()
  }

  const handleClickChangeSkip = () => {
    if (isValidDateTime && !showPreferredDateModal) {
      modal.onClose(() =>
        pickUpDeliveryFlow.start({
          onFinished: () => modalController.showModal('CartDrawer')
        })
      )
    } else {
      modal.onClose(resetAppointmentAction)
    }
  }

  const renderModalContent = () => {
    if (showPreferredDateModal) {
      return (
        <Container desktopHeight={desktopHeight}>
          <Modal.Header boxShadow='none'>
            <H2 bold normalised>
              {t('Your preferred _status_ date is no longer available', {
                status: isPickup ? t('pickup') : t('delivery')
              })}
            </H2>
          </Modal.Header>
          <StyledModalBody>
            <Flex>
              {t(
                '_status_ slots for _day_, _date_, _time_ are no longer available. Would you like to select another pickup date?',
                {
                  time: collectionTime,
                  status: isPickup ? t('Pickup') : t('Delivery'),
                  day: translatedCollectionDate,
                  date: formatDate({
                    t,
                    date: collectionDate,
                    locale: router.query.locale as string,
                    options: { year: 'numeric', month: 'short', day: 'numeric' }
                  })
                }
              )}
            </Flex>
          </StyledModalBody>
          <StyledModalFooter>
            <StyledLinkText heading onClick={handleClickChangeSkip}>
              {t('Skip')}
            </StyledLinkText>
            <StyledFormSubmitButton
              showPreferredDateModal={showPreferredDateModal}
            >
              {t('Select a _status_ Date', {
                status: isPickup ? t('Pickup') : t('Delivery')
              })}
            </StyledFormSubmitButton>
          </StyledModalFooter>
        </Container>
      )
    }

    return (
      <Container desktopHeight={desktopHeight}>
        <Modal.Header boxShadow='none'>
          <H2 bold normalised>
            {t('We noticed you left without completing your order')}
          </H2>
        </Modal.Header>
        <StyledModalBody>
          <Flex>
            {isValidDateTime
              ? t(
                  'Welcome back! Would you like to continue your _status_ order to _address_ for _day_, _time_?',
                  {
                    status: isPickup ? t('pick up') : t('delivery'),
                    address: isPickup ? storeAddress : deliveryAddress,
                    day: translatedCollectionDate,
                    time: collectionTime
                  }
                )
              : t(
                  'Welcome back! Your previously selected _status_ slot for today _time_ is no longer available. Would you like to select another delivery date?',
                  {
                    status: isPickup ? t('pick up') : t('delivery'),
                    time: collectionTime
                  }
                )}
          </Flex>
        </StyledModalBody>
        <StyledModalFooter>
          <StyledLinkText heading onClick={handleClickChangeSkip}>
            {isValidDateTime ? t('Change') : t('Skip')}
          </StyledLinkText>
          <StyledFormSubmitButton>
            {isValidDateTime && <ProgressBar px={px} />}
            {isValidDateTime
              ? t('Continue _timeLeft_', {
                  timeLeft: (timer.timeLeft * 0.001) % 60
                })
              : t('Select Now')}
          </StyledFormSubmitButton>
        </StyledModalFooter>
      </Container>
    )
  }

  return (
    <Modal
      {...modal}
      show={modal.show}
      slideInAnimation='right'
      withoutBackdrop
      Container={StyledModalContainer}
    >
      <WrapperForm
        onSubmit={(values, { setSubmitting }) => {
          setSubmitting(true)
          handlingFormSubmit()
          setSubmitting(false)
        }}
        justifyContent='left'
      >
        {renderModalContent()}
      </WrapperForm>
    </Modal>
  )
}

export default makeControlledModal(ReminderOrderModal, 'ReminderOrderModal')
